import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Typography,
  Button,
  Input,
  FormLabel,
  Alert,
} from '@mui/material';

const DynamicForm = ({ 
  fields = [], 
  values = {}, 
  onChange, 
  onSubmit, 
  loading = false,
  error = '',
  showSubmitButton = true 
}) => {
  const [formData, setFormData] = useState(values);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setFormData(values);
  }, [values]);

  const handleChange = (fieldName, value) => {
    const newFormData = { ...formData, [fieldName]: value };
    setFormData(newFormData);
    
    // Clear error for this field
    if (errors[fieldName]) {
      setErrors(prev => ({ ...prev, [fieldName]: '' }));
    }
    
    if (onChange) {
      onChange(newFormData);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    fields.forEach(field => {
      if (field.isRequired && (!formData[field.fieldName] || formData[field.fieldName] === '')) {
        newErrors[field.fieldName] = `${field.fieldLabel} is required`;
      }
      
      const value = formData[field.fieldName];
      if (value && value !== '') {
        switch (field.fieldType) {
          case 'number':
            if (isNaN(value)) {
              newErrors[field.fieldName] = `${field.fieldLabel} must be a number`;
            }
            break;
          case 'date':
            if (!Date.parse(value)) {
              newErrors[field.fieldName] = `${field.fieldLabel} must be a valid date`;
            }
            break;
        }
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm() && onSubmit) {
      onSubmit(formData);
    }
  };

  const renderField = (field) => {
    const value = formData[field.fieldName] || '';
    const hasError = !!errors[field.fieldName];
    const errorMessage = errors[field.fieldName];

    switch (field.fieldType) {
      case 'text':
        return (
          <TextField
            key={field.fieldName}
            fullWidth
            label={field.fieldLabel}
            name={field.fieldName}
            value={value}
            onChange={(e) => handleChange(field.fieldName, e.target.value)}
            placeholder={field.placeholder}
            helperText={errorMessage || field.helpText}
            error={hasError}
            required={field.isRequired}
            variant="outlined"
          />
        );

      case 'number':
        return (
          <TextField
            key={field.fieldName}
            fullWidth
            type="number"
            label={field.fieldLabel}
            name={field.fieldName}
            value={value}
            onChange={(e) => handleChange(field.fieldName, e.target.value)}
            placeholder={field.placeholder}
            helperText={errorMessage || field.helpText}
            error={hasError}
            required={field.isRequired}
            variant="outlined"
            inputProps={{
              min: field.validation?.min,
              max: field.validation?.max,
            }}
          />
        );

      case 'date':
        return (
          <TextField
            key={field.fieldName}
            fullWidth
            type="date"
            label={field.fieldLabel}
            name={field.fieldName}
            value={value}
            onChange={(e) => handleChange(field.fieldName, e.target.value)}
            helperText={errorMessage || field.helpText}
            error={hasError}
            required={field.isRequired}
            variant="outlined"
            InputLabelProps={{
              shrink: true,
            }}
          />
        );

      case 'select':
        return (
          <FormControl key={field.fieldName} fullWidth error={hasError} required={field.isRequired}>
            <InputLabel>{field.fieldLabel}</InputLabel>
            <Select
              value={value}
              onChange={(e) => handleChange(field.fieldName, e.target.value)}
              label={field.fieldLabel}
            >
              <MenuItem value="">
                <em>Select an option</em>
              </MenuItem>
              {field.options?.filter(opt => opt.isActive !== false).map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {(errorMessage || field.helpText) && (
              <FormHelperText>{errorMessage || field.helpText}</FormHelperText>
            )}
          </FormControl>
        );

      case 'multiselect':
        return (
          <FormControl key={field.fieldName} fullWidth error={hasError} required={field.isRequired}>
            <InputLabel>{field.fieldLabel}</InputLabel>
            <Select
              multiple
              value={Array.isArray(value) ? value : []}
              onChange={(e) => handleChange(field.fieldName, e.target.value)}
              label={field.fieldLabel}
              renderValue={(selected) => selected.join(', ')}
            >
              {field.options?.filter(opt => opt.isActive !== false).map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {(errorMessage || field.helpText) && (
              <FormHelperText>{errorMessage || field.helpText}</FormHelperText>
            )}
          </FormControl>
        );

      case 'attachment':
        return (
          <Box key={field.fieldName}>
            <FormLabel component="legend" required={field.isRequired}>
              {field.fieldLabel}
            </FormLabel>
            <Input
              type="file"
              fullWidth
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  // For now, just store the file name
                  // In a real implementation, you'd upload the file and store the URL
                  handleChange(field.fieldName, file.name);
                }
              }}
              inputProps={{
                accept: field.validation?.accept || '*/*',
              }}
            />
            {(errorMessage || field.helpText) && (
              <FormHelperText error={hasError}>
                {errorMessage || field.helpText}
              </FormHelperText>
            )}
            {value && (
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Selected: {value}
              </Typography>
            )}
          </Box>
        );

      default:
        return (
          <TextField
            key={field.fieldName}
            fullWidth
            label={field.fieldLabel}
            name={field.fieldName}
            value={value}
            onChange={(e) => handleChange(field.fieldName, e.target.value)}
            placeholder={field.placeholder}
            helperText={errorMessage || field.helpText}
            error={hasError}
            required={field.isRequired}
            variant="outlined"
          />
        );
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {error && (
        <Alert severity="error">
          {error}
        </Alert>
      )}
      
      {fields.map(renderField)}
      
      {showSubmitButton && (
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={loading}
          sx={{ mt: 2 }}
        >
          {loading ? 'Saving...' : 'Save Profile'}
        </Button>
      )}
    </Box>
  );
};

export default DynamicForm;